import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleUp } from "@fortawesome/free-solid-svg-icons";
import { keyframes } from 'styled-components';

import "./site_header.scss";
import styled from "styled-components";
import logo2 from "../../assets/logos/bolt.png";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { signOut } from "../../store/slices/authSlice";
import { toast } from "react-toastify";
import MobileNavBar from "./MobileNavBar";
import { isNavGroup, isNavItemActive, navEntriesFor } from "./navItems";
import { IS_BETA, AUTH_URL } from "../../config/env";
import { apiClient } from "../../api/client";
import { url } from "../../api";
import { StoreCount } from "../../features/marketplace";
import { useStoreOpen } from "../../features/storeSwitch/storeOpen";

interface BlockProps {
  active: boolean;
  pressed?: boolean;
}

interface LinkStyleProps {
  active?: boolean;
}

interface HeaderProps {
  bgColor?: string;
}

const fade = keyframes`
  0% { opacity: 1; }
  100% { opacity: 0;  }
`;

const dropIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Background = styled.div<{ z_idx: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index:${({ z_idx }) => z_idx ? -99 : 1};
  background: linear-gradient(to right, hsl(0, 70%, 85%), hsl(60, 70%, 85%), hsl(120, 70%, 85%), hsl(180, 70%, 85%), hsl(240, 70%, 85%), hsl(300, 70%, 85%), hsl(360, 70%, 85%));
  opacity: 1;
  animation: ${fade} 10s linear forwards;

  @media (max-width: 700px) {
    display: none;
  }
`;

const HeaderD = styled.header<HeaderProps>`
  position: relative;
  background: ${({ bgColor }) => bgColor || 'transparent'};
  z-index: ${({ bgColor }) => bgColor ? 3 : -99};

  @media (max-width: 700px) {
    background: transparent !important;
  }
`;

const BetaTag = styled.div`
  color: #000000;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 1rem;
  margin-left: 22px;
`;

const Header: React.FC = () => {
  const location = useLocation();
  const navEntries = navEntriesFor(useStoreOpen());
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth);
  const isAuthReady = user.bootstrapStatus === "ready";

  const isBeta = IS_BETA;

  // a dropdown opens on hover, or on click; a clicked one stays open until the
  // next navigation, a click outside the nav, or Escape
  const [pressedDropdown, setPressedDropdown] = useState("");
  // after picking an item the pointer is still over the group, which would keep
  // the hover dropdown up; hold it shut until the pointer leaves
  const [hoverSuppressed, setHoverSuppressed] = useState("");
  const navRef = useRef<HTMLUListElement>(null);
  const [signOutIsHovered, setSignOutIsHovered] = useState(false);
  const [useLegacyLayout, setUseLegacyLayout] = useState(() => {
    const stored = localStorage.getItem("useLegacyLayout");
    return stored === "true";
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem("useLegacyLayout");
      setUseLegacyLayout(stored === "true");
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("legacyLayoutChanged", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("legacyLayoutChanged", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    setPressedDropdown("");
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!pressedDropdown) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setPressedDropdown("");
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPressedDropdown("");
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [pressedDropdown]);

  const navigate = useNavigate();
  function handleClick() {
    navigate("/");
  }

  const handleSignOut = async () => {
    try {
      await apiClient.post(`${url}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.warn("Failed to revoke session on server:", error);
    }
    dispatch(signOut());
    toast("Hope to see you again soon!");

    // redirect to Auth's RP-initiated logout to destroy the SSO session,
    // otherwise the next login auto-authenticates without showing a login form
    const postLogoutUri = encodeURIComponent(window.location.origin);
    window.location.href = `${AUTH_URL}/oauth/logout?client_id=n450s_web&post_logout_redirect_uri=${postLogoutUri}`;
  };

  const [bgColor, setBgColor] = useState('');
  const [z_idx, setZ_idx] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      if (isBeta) {
        // set fixed indigo background in beta mode
        setBgColor('#9b96fd');
        setZ_idx(true);
        return;
      }
      let hue;
      do {
        hue = Math.floor(Math.random() * 360);
      } while (hue >= 90 && hue <= 150); // Exclude green hues from 90 to 150
      const saturation = 90;
      const lightness = 95;
      setBgColor(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
      setZ_idx(true);
    }, 0);
  }, [isBeta]);

  if (location.pathname === "/login" || location.pathname === "/register" || location.pathname === "/party") {
    return null;
  }
  return (
    <>
      <div className="header-container">
        <HeaderD className="my-header" bgColor={bgColor}>
          {!isBeta && <Background z_idx={z_idx} />}
          <nav className="header-nav">
            <div className="logo-container">
              {isBeta && <BetaTag>beta version</BetaTag>}
              <img
                className="my-logo"
                src={logo2}//src={outlineLogo}
                onClick={handleClick}
                alt="logo"
              />
              {/* gay month special */}
            </div>
            <ul ref={navRef} className={`header-ul ${useLegacyLayout ? "" : "hide-on-mobile"}`}>
              {navEntries.map((entry) => {
                if (isNavGroup(entry)) {
                  const { name, items } = entry;
                  const isPressed = pressedDropdown === name;
                  const isActive = items.some((item) => isNavItemActive(location.pathname, item));

                  return (
                    <div className="dropdown-div" key={name}>
                      <DropdownWrapper
                        pressed={isPressed}
                        $suppressHover={hoverSuppressed === name}
                        onMouseLeave={() => {
                          if (hoverSuppressed === name) setHoverSuppressed("");
                        }}
                      >
                        <DropdownButton
                          type="button"
                          onClick={() => {
                            setHoverSuppressed("");
                            setPressedDropdown(isPressed ? "" : name);
                          }}
                          active={isActive}
                          pressed={isPressed}
                          aria-expanded={isPressed}
                          aria-haspopup="true"
                        >
                          {name}
                          {items.some((item) => item.to === "/marketplace") && <StoreCount />}{" "}
                          <FontAwesomeIcon
                            style={{
                              marginRight: "3px",
                              alignItems: "center",
                              color: "#3f3f3fff",
                            }}
                            icon={
                              isPressed ? faAngleUp as any : faAngleDown as any
                            }
                            aria-hidden="true"
                          />
                        </DropdownButton>

                        <DropdownBlock
                          pressed={isPressed}
                          active={isActive}
                          className={"dropdown"}
                        >
                          {items.map((item, index) => (
                            <LinkStyle key={item.name} to={item.to}>
                              <BlockSub
                                className={"block" + item.name + " dropdown-item"}
                                onClick={() => {
                                  setPressedDropdown("");
                                  setHoverSuppressed(name);
                                }}
                                style={{
                                  borderBottom: index === items.length - 1 ? 'none' : '1px solid #ccc',
                                }}
                                active={isNavItemActive(location.pathname, item)}
                              >
                                {item.name}
                                {item.to === "/marketplace" && <StoreCount />}
                              </BlockSub>
                            </LinkStyle>
                          ))}
                        </DropdownBlock>
                      </DropdownWrapper>
                    </div>
                  );
                }

                return (
                  <LinkStyleSingle key={entry.name} to={entry.to}>
                    <Block
                      className={"block" + entry.name}
                      active={isNavItemActive(location.pathname, entry)}
                    >
                      {entry.name}
                      {entry.to === "/marketplace" && <StoreCount />}
                    </Block>
                  </LinkStyleSingle>
                );
              })}
              <div className="auth">
                {!isAuthReady ? null : user._id ? (
                  <>
                    {/* the hand chip (features/respectCards HandQuickButton) is
                        intentionally hidden until the cards launch, same as the
                        home surfaces */}
                    <LinkStyleAuth to="/profile">
                      <BlockAuth active={location.pathname === "/profile"}>
                        profile
                      </BlockAuth>
                    </LinkStyleAuth>
                    <button
                      className="sign-out"
                      onClick={handleSignOut}
                      onMouseEnter={() => setSignOutIsHovered(true)}
                      onMouseLeave={() => setSignOutIsHovered(false)}
                    >
                      {signOutIsHovered ? `😢` : "logout"}
                    </button>
                  </>
                ) : (
                  <>
                    <LinkStyleAuth to="/register">
                      <BlockAuth active={location.pathname === "/register"}>
                        signup
                      </BlockAuth>
                    </LinkStyleAuth>
                    <LinkStyleAuth to="/login">
                      <BlockAuth active={location.pathname === "/login"}>
                        login
                      </BlockAuth>
                    </LinkStyleAuth>
                  </>
                )}
              </div>
            </ul>
          </nav>
        </HeaderD>
      </div>
      {!useLegacyLayout && <MobileNavBar />}
    </>
  );
};

// desktop items grow on hover (padding eases in over 0.2s). the grow shoves
// the row sideways a little, which is the point. at 700px and below the
// padding is pinned so nothing moves.
const Block = styled.div<BlockProps>`
  background-color: ${(props) => (props.active ? "#9beece" : "white")};
  transition: padding 0.2s, background-color 0.15s;
  &:hover {
    background-color: ${(props) => (props.active ? "#9beece" : "#d4f9eb")};
    padding: 3px;
  }
  @media (max-width: 700px) {
    margin-top: 5px;
    margin-bottom: 5px;
    padding: 10px;
  }
`;

const DropdownButton = styled.button<BlockProps>`
  background-color: ${(props) => (props.active ? "#9beece" : "white")};
  box-shadow: ${(props) => (props.pressed ? "var(--hm-shadow-sm)" : "none")};
  border: ${(props) => (props.pressed ? "var(--hm-border-width) solid #466a5d" : "0px")};
  padding: ${(props) => (props.pressed ? "3px" : "0px")};
  transition: padding 0.2s, background-color 0.15s, box-shadow 0.15s;
  font-size: 1rem;
  color: ${(props) => (props.active ? "black" : "#3f3f3fff")};
  align-items: center;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => (props.active ? "#9beece" : "#d4f9eb")};
    padding: ${(props) => (props.pressed ? "4px" : "3px")};
    color: black;
  }

  @media (max-width: 700px) {
    margin-top: 5px;
    margin-bottom: 5px;
    width: 100%;
    padding: 10px;
  }
`;

const BlockSub = styled.div<BlockProps>`
  background-color: ${(props) => (props.active ? "#466a5d" : "white")};
  color: ${(props) => (props.active ? "#dcdcdc" : "#383838")};
  transition: padding 0.2s, background-color 0.15s;
  &:hover {
    background-color: ${(props) => (props.active ? "#466a5d" : "#9beece")};
    padding: 3px;
  }
  @media (max-width: 700px) {
    padding: 10px;
  }
`;

const DropdownBlock = styled.div<BlockProps>`
  display: ${(props) => (props.pressed ? "block" : "none")};
  position: absolute;
  width: 9rem;
  z-index: 10;
  background-color: white;
  box-shadow: var(--hm-shadow);
  animation: ${dropIn} 0.15s ease-out;
  @media (max-width: 700px) {
    left: -50%;
  }
`;

const DropdownWrapper = styled.div<{ pressed?: boolean; $suppressHover?: boolean }>`
  position: relative;
  &:hover > div.dropdown {
    display: ${(props) => (props.$suppressHover && !props.pressed ? "none" : "block")};
    @media (max-width: 700px) {
      display: ${(props) => (props.pressed ? "block" : "none")};
    }
  }
`;

const BlockAuth = styled.div<BlockProps>`
  /* background-color: #fb8ff9; */
  margin: 10px;
  /* border: ${(props) => (props.active ? "3px solid transparent" : "3px solid transparent")}; */
  font-weight: ${(props) => (props.active ? 800 : 200)};
  /* border: ${(props) => (props.active ? "3px" : "0px")}; */
  font-size: ${(props) => (props.active ? "1.1rem" : "1rem")};
  transition: all 0.2s;
  /* width: 60px; */
  /* display: list-item; */
  &:hover {
    font-weight: ${(props) => (props.active ? 900 : 400)};
    font-size: ${(props) => (props.active ? "1.1rem" : "1rem")};
    filter: drop-shadow(0px 0px 1px #000000);
    /* width: initial; */
  }
  @media (max-width: 700px) {
    padding: 10px;
    /* width:100%; */
    &:hover {
      padding: 10px;
    }
  }
`;

const LinkStyleAuth = styled(NavLink) <LinkStyleProps>`
  color: ${(props) => (props.active ? "black" : "#3f3f3fff")};
  text-decoration: none;
  font-size: medium;
  /* font-weight: 200; */
  min-width: 75px;
  /* display: inline-block; */
  &:hover {
    color: black;
    /* font-weight: 400; */
    /* filter: drop-shadow(0px 0px 1px #000000); */
  }
  &:active {
    color: #ff4be1;
    /* font-weight: 400; */
  }
  &:focus {
    color: black;
  }
`;

const LinkStyle = styled(NavLink) <LinkStyleProps>`
  color: ${(props) => (props.active ? "black" : "#3f3f3fff")};
  text-decoration: none;

  &:hover {
    color: black;
  }
  &:active {
    color: #ff4be1;
  }
  &:selected {
    background-color: #9beece;
  }
  &:focus {
    color: black;
  }
`;
const LinkStyleSingle = styled(NavLink) <LinkStyleProps>`
  color: ${(props) => (props.active ? "black" : "#3f3f3fff")};
  text-decoration: none;
  align-items: center;
  margin: 10px;

  &:hover {
    color: black;
  }
  &:active {
    color: #ff4be1;
  }
  &:selected {
    background-color: #9beece;
  }
  &:focus {
    color: black;
  }
  @media (max-width: 700px) {
    margin: 0px;
  }
`;

export default Header;
